import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "../../lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const TYPE_LABELS = { general: "General", product: "Product Enquiry", wet_belt: "Wet Belt", inspection: "Inspection", car_sale: "Car Sale" };
const fmtDT = (iso) => (iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-");

export default function Enquiries() {
  const [enquiries, setEnquiries] = useState([]);
  const [status, setStatus] = useState("all");

  const load = useCallback(() => {
    const params = status !== "all" ? { status } : {};
    api.get("/admin/enquiries", { params }).then((res) => setEnquiries(res.data)).catch(() => {});
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const mark = async (e, s) => {
    try {
      await api.put(`/admin/enquiries/${e.id}`, { status: s });
      toast.success(`Enquiry marked ${s}.`);
      load();
    } catch (err) {
      toast.error("Could not update enquiry.");
    }
  };

  return (
    <div data-testid="admin-enquiries">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-extrabold text-white text-2xl tracking-tight">Enquiries</h1>
          <p className="text-pale/50 text-sm mt-1">{enquiries.length} enquir{enquiries.length === 1 ? "y" : "ies"}.</p>
        </div>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger data-testid="enquiries-status-filter" className="sm:w-48 bg-navy border-deep text-white h-10">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="NEW">NEW</SelectItem>
            <SelectItem value="RESOLVED">RESOLVED</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-6 space-y-4">
        {enquiries.map((e) => (
          <div key={e.id} data-testid={`enquiry-card-${e.id}`} className="bg-navy border border-deep rounded-lg p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3 flex-wrap">
                <p className="font-heading font-bold text-white">{e.name}</p>
                <span className="status-chip bg-deep/60 text-pale/70 border border-deep">{TYPE_LABELS[e.type] || e.type}</span>
                <span className={`status-chip ${e.status === "NEW" ? "bg-sky/15 text-sky border border-sky/30" : "bg-deep/60 text-pale/50 border border-deep"}`}>{e.status}</span>
              </div>
              <p className="text-xs text-pale/40">{fmtDT(e.created_at)}</p>
            </div>
            {e.product_title && (
              <p className="text-xs text-sky mt-2.5">Re: {e.product_title}</p>
            )}
            <p className="text-sm text-pale/80 leading-relaxed mt-2.5 bg-deep/30 rounded p-3.5">{e.message}</p>
            <div className="flex flex-wrap items-center justify-between gap-3 mt-3.5">
              <div className="flex gap-5 text-xs text-pale/60">
                {e.phone && <a href={`tel:${e.phone.replace(/\s/g, "")}`} className="hover:text-sky transition-colors">{e.phone}</a>}
                {e.email && <a href={`mailto:${e.email}`} className="hover:text-sky transition-colors">{e.email}</a>}
              </div>
              {e.status === "NEW" ? (
                <button onClick={() => mark(e, "RESOLVED")} data-testid={`resolve-enquiry-${e.id}`} className="text-xs font-heading font-bold text-sky hover:text-white tracking-wide transition-colors">
                  MARK RESOLVED
                </button>
              ) : (
                <button onClick={() => mark(e, "NEW")} data-testid={`reopen-enquiry-${e.id}`} className="text-xs font-heading font-bold text-pale/50 hover:text-white tracking-wide transition-colors">
                  REOPEN
                </button>
              )}
            </div>
          </div>
        ))}
        {enquiries.length === 0 && (
          <div className="bg-navy border border-deep rounded-lg p-12 text-center text-pale/40">No enquiries found.</div>
        )}
      </div>
    </div>
  );
}
